import React, { useMemo } from 'react'
import { Button, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery, useTheme } from '@material-ui/core'
import { useRecoilValue } from 'recoil'
import { gregorianToJalali, thousandsSeparate, toFarsiDigits } from '../../assets/utils'
import { useReportFilter, ReportToolbar } from '../../assets/reportFilters'
import { exportExcel, reportMetaNotes, fetchReportQr } from '../../assets/excelUtils'
import { treasuryBillsState } from '../../pages/contractor/atoms'

import jsPDF from 'jspdf'
import { userInfoState } from '../../recoil/atoms'
import { vazirFont } from '../../assets/fonts/vazir-font-v18.0.0/Vazir'
import { LoadMoreButton, useIncrementalList } from '../../hooks/useIncrementalList'
import { addPdfFooter } from '../../assets/reportFooter'

const printRequest = (contractee, userInfo, title) => {
    const doc = jsPDF()
    const marginTop = 70
    doc.addFileToVFS("Vazir.ttf", vazirFont);
    doc.addFont("Vazir.ttf", "Vazir", "normal");
    doc.setFont('Vazir')
    doc.text(200, marginTop, contractee, { align: 'right' })
    let text = 'اینجانب ' + userInfo.name + ' '
    text += 'به شماره ثبت ' + toFarsiDigits(userInfo.registration_number) + ' '
    text += 'و کد اقتصادی ' + toFarsiDigits(userInfo.economic_code) + ' '
    text += 'و کد معاملاتی ' + toFarsiDigits(userInfo.brokerage_account) + ' '
    text += 'تقاضای دریافت اسناد خزانه اسلامی با نماد معاملاتی ' + toFarsiDigits(title) + ' '
    text += 'را مطابق قوانین و مقررات مربوطه از آن دستگاه دارم '
    text += 'ضمناً آن دستگاه اجرایی و سایر مراجع مربوط مجازند در هر مرحله از صدور اسناد خزانه اسلامی که تشخیص دهند اسناد مذکور به اشتباه یا بیش از رقم مقرر صادر شده است '
    text += 'نسبت به ابطال اسناد مذکور اقدام نمایند و بدینوسیله حق هر گونه ادعایی در این خصوص را از خود سلب مینمایم'
    doc.text(200, marginTop + 15, text, { maxWidth: 240, align: 'right' })
    doc.text(70 + userInfo.name.length, marginTop + 100, userInfo.name, { maxWidth: 240, align: 'right' })
    addPdfFooter(doc)
    doc.output('dataurlnewwindow', 'request.pdf')
}

// خروجی اکسل اسناد خزانه — همان ۳۲ ستون جدول با اعداد واقعی
const exportTreasuryExcel = async (data, userInfo, contract) => {
    const qr = contract && contract.id ? await fetchReportQr(contract.id) : null
    const headers = [
        'ردیف', 'نوع وضعیت', 'نوع اسناد خزانه', 'عنوان اسناد', 'درصد سود', 'تاریخ سررسید',
        'شماره درخواست', 'شماره صورت وضعیت', 'تاریخ صورت وضعیت', 'مبلغ صورت وضعیت', 'مبلغ پرداختی',
        'اضافات سهم دولت', 'جمع', 'کسر پیش پرداخت', 'کسر احکام قضایی', 'زمان انتظار به روز',
        'توضیحات', 'خالص پیمانکار', 'حفظ قدرت خرید پیمانکار', 'مالیات بر ارزش افزوده',
        'زمان انتظار مالیات بر ارزش افزوده', 'حفظ قدرت خرید مالیات بر ارزش افزوده',
        'تعداد اوراق پیمانکار', 'تعداد اوراق پیش پرداخت', 'گیرنده اوراق پیش پرداخت',
        'بیمه', 'زمان انتظار بیمه', 'حفظ قدرت خرید بیمه', 'تعداد اوراق بیمه',
        'تعداد اوراق سپرده', 'کسر شده از اوراق پیمانکار', 'درخواست',
    ]
    const rows = data.map((row, index) => [
        index + 1,
        row.payment_type,
        row.document_type,
        row.title,
        row.benefit_percent,
        gregorianToJalali(row.expire_date),
        row.letter_number,
        row.invoice_number,
        gregorianToJalali(row.invoice_date),
        row.request_amount,
        row.invoice_amount,
        row.addition,
        row.sum_pay,
        row.prepayment,
        row.statement,
        row.waiting_days,
        row.remark,
        row.net_payment,
        row.interest_amount,
        row.value_added_tax,
        row.addition_day,
        row.value_added_tax_interest,
        row.quantity,
        row.prepayment_quantity,
        row.prepayment_receiver,
        row.sum_net_tamin,
        row.tamin_day,
        row.sum_interest_tamin,
        row.insurance_quantity,
        row.deposit_quantity,
        row.receiver_quantity,
        '—',
    ])
    exportExcel({
        filename: 'treasury_bills.xlsx',
        title: `اسناد خزانه — ${userInfo && userInfo.name ? userInfo.name : ''}`,
        headers,
        rows,
        notes: reportMetaNotes(),
        qrDataUri: qr,
        totalRow: (() => {
            const tr = headers.map(() => '')
            tr[0] = 'جمع'
            tr[9] = data.reduce((a, r) => a + (Number(r.request_amount) || 0), 0)   // مبلغ صورت وضعیت
            tr[10] = data.reduce((a, r) => a + (Number(r.invoice_amount) || 0), 0)  // مبلغ پرداختی
            tr[17] = data.reduce((a, r) => a + (Number(r.net_payment) || 0), 0)     // خالص پیمانکار
            return tr
        })(),
    })
}

const TreasuryBills = (props) => {
    const treasuryBills = useRecoilValue(treasuryBillsState)
    const userInfo = useRecoilValue(userInfoState)
    const extract = useMemo(() => ({
        search: (row) => [
            row.invoice_number, row.letter_number, row.invoice_date, row.request_amount,
            row.invoice_amount, row.sum_pay, row.net_payment, row.title,
            row.document_type, row.payment_type, row.remark,
        ],
        // سال مالی از تاریخ صورت وضعیت (شمسی: «YYYY/MM/DD»)
        year: (row) => {
            const j = gregorianToJalali(row.invoice_date)
            return j ? String(j).split('/')[0] : null
        },
    }), [])
    const { search, setSearch, year, setYear, years, filtered } = useReportFilter(
        Array.isArray(treasuryBills && treasuryBills.data) ? treasuryBills.data : [], extract)

    // رندرِ تدریجی + حذفِ جعبهٔ اسکرولِ تودرتو در موبایل
    const { visible, hasMore, loadMore, remaining } = useIncrementalList(filtered, {
        step: 50,
        resetKey: `${search}|${year}`,
    })
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    return (
        <>
            <ReportToolbar
                search={search} setSearch={setSearch}
                year={year} setYear={setYear} years={years}
                rowsCount={Array.isArray(treasuryBills && treasuryBills.data) ? treasuryBills.data.length : 0}
                filteredCount={filtered.length}
            />
            <Box display='flex' justifyContent='flex-end' style={{ marginBottom: 8 }}>
                <Button variant='contained' color='secondary' onClick={() => exportTreasuryExcel(filtered, userInfo, props.contract)}>
                    دانلود اکسل
                </Button>
            </Box>
            <TableContainer
                elevation={5}
                component={Paper}
                style={isMobile ? undefined : { maxHeight: '450px' }}
            >
            <Table stickyHeader={!isMobile}>
                <TableHead>
                    <TableRow>
                        <TableCell align='center'><strong>ردیف</strong></TableCell>
                        <TableCell align='center'><strong>نوع وضعیت</strong></TableCell>
                        <TableCell align='center'><strong>نوع اسناد خزانه</strong></TableCell>
                        <TableCell align='center'><strong>عنوان اسناد</strong></TableCell>
                        <TableCell align='center'><strong>درصد سود</strong></TableCell>
                        <TableCell align='center'><strong>تاریخ سررسید</strong></TableCell>
                        <TableCell align='center'><strong>شماره درخواست</strong></TableCell>
                        <TableCell align='center'><strong>شماره صورت وضعیت</strong></TableCell>
                        <TableCell align='center'><strong>تاریخ صورت وضعیت</strong></TableCell>
                        <TableCell align='center'><strong>مبلغ صورت وضعیت</strong></TableCell>
                        <TableCell align='center'><strong>مبلغ پرداختی</strong></TableCell>
                        <TableCell align='center'><strong>اضافات سهم دولت</strong></TableCell>
                        <TableCell align='center'><strong>جمع</strong></TableCell>
                        <TableCell align='center'><strong>کسر پیش پرداخت</strong></TableCell>
                        <TableCell align='center'><strong>کسر احکام قضایی</strong></TableCell>
                        <TableCell align='center'><strong>زمان انتظار به روز</strong></TableCell>
                        <TableCell align='center'><strong>توضیحات</strong></TableCell>
                        <TableCell align='center'><strong>خالص پیمانکار</strong></TableCell>
                        <TableCell align='center'><strong>حفظ قدرت خرید پیمانکار</strong></TableCell>
                        <TableCell align='center'><strong>مالیات بر ارزش افزوده</strong></TableCell>
                        <TableCell align='center'><strong>زمان انتظار مالیات بر ارزش افزوده</strong></TableCell>
                        <TableCell align='center'><strong>حفظ قدرت خرید مالیات بر ارزش افزوده</strong></TableCell>
                        <TableCell align='center'><strong>تعداد اوراق پیمانکار</strong></TableCell>
                        <TableCell align='center'><strong>تعداد اوراق پیش پرداخت</strong></TableCell>
                        <TableCell align='center'><strong>گیرنده اوراق پیش پرداخت</strong></TableCell>
                        <TableCell align='center'><strong>بیمه</strong></TableCell>
                        <TableCell align='center'><strong>زمان انتظار بیمه</strong></TableCell>
                        <TableCell align='center'><strong>حفظ قدرت خرید بیمه</strong></TableCell>
                        <TableCell align='center'><strong>تعداد اوراق بیمه</strong></TableCell>
                        <TableCell align='center'><strong>تعداد اوراق سپرده</strong></TableCell>
                        <TableCell align='center'><strong>کسر شده از اوراق پیمانکار</strong></TableCell>
                        <TableCell align='center'><strong>درخواست</strong></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {visible.map((row, index) => (
                        <TableRow key={row.id}>
                            <TableCell align='center'><strong>{toFarsiDigits(index + 1)}</strong></TableCell>
                            <TableCell align='center'>{toFarsiDigits(row.payment_type)}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(row.document_type)}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(row.title)}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.benefit_percent))}%</TableCell>
                            <TableCell align='center'>{gregorianToJalali(row.expire_date)}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(row.letter_number)}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(row.invoice_number)}</TableCell>
                            <TableCell align='center'>{gregorianToJalali(row.invoice_date)}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.request_amount))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.invoice_amount))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.addition))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.sum_pay))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.prepayment))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.statement))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.waiting_days))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(row.remark)}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.net_payment))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.interest_amount))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.value_added_tax))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.addition_day))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.value_added_tax_interest))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.quantity))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.prepayment_quantity))}</TableCell>
                            <TableCell align='center'>{row.prepayment_receiver}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.sum_net_tamin))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.tamin_day))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.sum_interest_tamin))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.insurance_quantity))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.deposit_quantity))}</TableCell>
                            <TableCell align='center'>{toFarsiDigits(thousandsSeparate(row.receiver_quantity))}</TableCell>
                            <TableCell align='center'>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={() => printRequest(
                                        treasuryBills.contract.contractee,
                                        userInfo,
                                        row.title
                                    )}
                                >
                                    درخواست
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            </TableContainer>
            <LoadMoreButton hasMore={hasMore} remaining={remaining} onClick={loadMore} />
        </>
    )
}
export default TreasuryBills